import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  ChakraProvider,
  Container,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import AdminHeader from '../components/AdminHeader';
import { loadWhatsAppLinksForAdmin, saveWhatsAppLinks, whatsAppFieldError } from '../settings/whatsappApi';

function WhatsAppSettings() {
  const navigate = useNavigate();
  const toast = useToast();
  const [groupUrl, setGroupUrl] = useState('');
  const [channelUrl, setChannelUrl] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSavedLinks, setHasSavedLinks] = useState(false);

  useEffect(() => {
    document.title = 'WhatsApp Links';
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) {
      navigate('/Admin');
      return undefined;
    }
    let active = true;
    loadWhatsAppLinksForAdmin()
      .then((links) => {
        if (!active) return;
        setGroupUrl(links.group_url);
        setChannelUrl(links.channel_url);
        setHasSavedLinks(Boolean(links.group_url || links.channel_url));
      })
      .catch((error) => {
        if (!active) return;
        toast({
          description: error.message || 'Unable to load WhatsApp links.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'bottom',
        });
        if (/authentication/i.test(error.message || '')) navigate('/Admin');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [navigate, toast]);

  const handleSave = async () => {
    const nextErrors = {
      group_url: whatsAppFieldError('group', groupUrl),
      channel_url: whatsAppFieldError('channel', channelUrl),
    };
    const visibleErrors = Object.fromEntries(Object.entries(nextErrors).filter(([, message]) => message));
    setErrors(visibleErrors);
    if (Object.keys(visibleErrors).length) return;

    setIsSaving(true);
    try {
      const result = await saveWhatsAppLinks({ group_url: groupUrl, channel_url: channelUrl });
      setGroupUrl(result.group_url);
      setChannelUrl(result.channel_url);
      setHasSavedLinks(Boolean(result.group_url || result.channel_url));
      setErrors({});
      toast({
        description: result.message || 'WhatsApp links saved.',
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      setErrors(error.fields || {});
      toast({
        description: error.message || 'Unable to save WhatsApp links.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      if (/authentication/i.test(error.message || '')) navigate('/Admin');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ChakraProvider>
      <Box minH="100vh" bg="gray.50">
        <AdminHeader activeTab="whatsapp" title="WhatsApp Links" />
        <Container maxW="3xl" pb={16}>
          <Card borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
            <CardBody>
              <VStack align="stretch" spacing={6}>
                <Box>
                  <Text fontWeight="700" color="gray.800">Community links</Text>
                  <Text color="gray.600" fontSize="sm" mt={1}>
                    These links appear at the top and bottom of every published case study. Leave a field empty to hide that button.
                  </Text>
                </Box>
                <FormControl isInvalid={Boolean(errors.group_url)}>
                  <FormLabel fontWeight="600" color="gray.700">WhatsApp Group (Chat)</FormLabel>
                  <Input
                    value={groupUrl}
                    onChange={(event) => setGroupUrl(event.target.value)}
                    placeholder="https://chat.whatsapp.com/…"
                    borderRadius="md"
                    border="2px solid"
                    borderColor="gray.200"
                    isDisabled={isLoading || isSaving}
                  />
                  <FormHelperText>Paste the group invitation link.</FormHelperText>
                  <FormErrorMessage>{errors.group_url}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.channel_url)}>
                  <FormLabel fontWeight="600" color="gray.700">WhatsApp Channel</FormLabel>
                  <Input
                    value={channelUrl}
                    onChange={(event) => setChannelUrl(event.target.value)}
                    placeholder="https://whatsapp.com/channel/…"
                    borderRadius="md"
                    border="2px solid"
                    borderColor="gray.200"
                    isDisabled={isLoading || isSaving}
                  />
                  <FormHelperText>Paste the channel follow link.</FormHelperText>
                  <FormErrorMessage>{errors.channel_url}</FormErrorMessage>
                </FormControl>
                <Button
                  alignSelf={{ base: 'stretch', sm: 'flex-start' }}
                  colorScheme="green"
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={isLoading}
                  borderRadius="md"
                  px={8}
                >
                  {hasSavedLinks ? 'Update links' : 'Save links'}
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    </ChakraProvider>
  );
}

export default WhatsAppSettings;
